import React, { useEffect, useState } from "react";
import {
  Table,
  Input,
  Button,
  Modal,
  Form,
  Select,
  Tag,
  Space,
  Popconfirm,
  Row,
  Col,
} from "antd";
import { PlusOutlined, EditOutlined, DeleteOutlined } from "@ant-design/icons";
import { useDispatch, useSelector } from "react-redux";
import {
  fetchRoles,
  createRole,
  updateRole,
  deleteRole,
} from "../../Redux/Slice/Admin/Roles";
import { toast } from "react-toastify";
import { fetchBranches } from "../../Redux/Slice/Admin/Branch";

const { Option } = Select;

const PERMISSION_OPTIONS = [
  "DASHBOARD",
  "PATIENT",
  "APPOINMENT",
  "BRANCHES",
  "SPECIALITIES",
  "ROLES",
  "DOCTOR",
];

const RoleManagement = () => {
  const dispatch = useDispatch();
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editingRole, setEditingRole] = useState(null);
  const [form] = Form.useForm();

  const { data: roleData, loading: roleLoading } = useSelector(
    (state) => state.role.fetch
  );

  const { data: branchData } = useSelector((state) => state.branch.fetch);

  console.log("branchData", branchData);

  const {
    data: createData,
    error: createError,
    loading: createLoading,
  } = useSelector((state) => state.role.create);

  const {
    data: updateData,
    error: updateError,
    loading: updateLoading,
  } = useSelector((state) => state.role.update);
  const {
    data: deleteData,
    error: deleteError,
    loading: deleteLoading,
  } = useSelector((state) => state.role.delete);

  useEffect(() => {
    dispatch(fetchRoles());
  }, [dispatch]);

  useEffect(() => {
    dispatch(fetchBranches());
  }, [dispatch]);

  useEffect(() => {
    if (createData) {
      if (createData.status === true) {
        toast.success(createData.message || "Created successfully!");
        dispatch(fetchRoles());
        setIsModalOpen(false);
        form.resetFields();
      }
    }
    if (createError) {
      toast.error(createError.message || "Creation failed!");
    }
  }, [createError, createData, dispatch, form]);

  useEffect(() => {
    if (updateData) {
      if (updateData.status === true) {
        toast.success(updateData.message || "Updated successfully!");
        dispatch(fetchRoles());
        setIsModalOpen(false);
        form.resetFields();
      }
    }
    if (updateError) {
      toast.error(updateError.message || "Update failed!");
    }
  }, [updateError, updateData, dispatch, form]);

  useEffect(() => {
    if (deleteData) {
      if (deleteData.status === true) {
        toast.success(deleteData.message || "Deleted successfully!");
        dispatch(fetchRoles());
      }
    }
    if (deleteError) {
      toast.error(deleteError.message || "Deletion failed!");
    }
  }, [deleteError, deleteData, dispatch]);

  // Open modal for Create/Edit
  const openModal = (record = null) => {
    setEditingRole(record);
    if (record) {
      // Pre-fill form (map permissions array of objects → array of module strings)
      form.setFieldsValue({
        name: record.name,
        username: record.username,
        password: record.password,
        emailId: record.emailId,
        phoneNo: record.phoneNo,
        role: record.role,
        permissions: record.permissions?.map((p) => p.module) || [],
        branchIds: record.branches?.map((b) => b.branchId) || [],
      });
    } else {
      form.resetFields();
    }
    setIsModalOpen(true);
  };

  const handleOk = async () => {
    try {
      const values = await form.validateFields();

      // Build payload
      const payload = {
        name: values.name,
        username: values.username,
        password: values.password,
        emailId: values.emailId,
        phoneNo: values.phoneNo,
        role: values.role,
        branchIds: values.branchIds || [],
        permissions: (values.permissions || []).map((module) => ({
          module,
          hasAccess: true,
        })),
      };

      if (editingRole) {
        dispatch(updateRole({ userId: editingRole.userId, ...payload }));
      } else {
        dispatch(createRole(payload));
      }
    } catch (err) {
      console.error("Validation or API error:", err);
    }
  };

  const handleDelete = async (record) => {
    dispatch(deleteRole(record.userId));
  };

  const columns = [
    { title: "Name", dataIndex: "name" },
    { title: "Username", dataIndex: "username" },
    { title: "Role", dataIndex: "role" },
    {
      title: "Branches",
      dataIndex: "branches",
      render: (_, record) => (
        <>
          {record.branches?.map((b) => (
            <Tag color="green" key={b.branchId}>
              {b.branchName}
            </Tag>
          ))}
        </>
      ),
    },
    {
      title: "Permissions",
      dataIndex: "permissions",
      render: (_, record) => (
        <>
          {record.permissions?.map((perm) => (
            <Tag color="blue" key={perm.module}>
              {perm.module}
            </Tag>
          ))}
        </>
      ),
    },
    {
      title: "Actions",
      render: (_, record) => (
        <Space>
          <Button
            type="text"
            icon={<EditOutlined />}
            onClick={() => openModal(record)}
          />
          <Popconfirm
            title="Are you sure to delete this role?"
            okText="Yes"
            cancelText="No"
            onConfirm={() => handleDelete(record)}
            disabled={deleteLoading}
          >
            <Button type="text" danger icon={<DeleteOutlined />} />
          </Popconfirm>
        </Space>
      ),
    },
  ];

  return (
    <div style={{ padding: 20 }}>
      <div
        style={{
          display: "flex",
          justifyContent: "space-between",
          marginBottom: 16,
        }}
      >
        <h2>Admin </h2>
        <Button
          type="primary"
          icon={<PlusOutlined />}
          onClick={() => openModal()}
        >
          Create
        </Button>
      </div>

      <Table
        rowKey="id"
        dataSource={roleData?.data?.roles || []}
        columns={columns}
        pagination={false}
        loading={roleLoading}
      />

      {/* Create/Edit Modal */}
      <Modal
        title={editingRole ? "Edit Role" : "Create Role"}
        open={isModalOpen}
        onOk={handleOk}
        onCancel={() => setIsModalOpen(false)}
        okText={editingRole ? "Update" : "Create"}
        cancelText="Cancel"
        maskClosable={false}
        style={{ top: 30 }}
        width={600}
        confirmLoading={editingRole ? updateLoading : createLoading}
      >
        <Form form={form} layout="vertical">
          <Row gutter={16}>
            <Col span={12}>
              <Form.Item
                name="name"
                label="Full Name"
                rules={[{ required: true, message: "Please enter full name" }]}
              >
                <Input placeholder="Enter full name" />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item
                name="username"
                label="Username"
                rules={[{ required: true, message: "Please enter username" }]}
              >
                <Input placeholder="Enter username" />
              </Form.Item>
            </Col>
          </Row>

          <Row gutter={16}>
            <Col span={12}>
              <Form.Item
                name="password"
                label="Password"
                rules={[{ required: true, message: "Please enter password" }]}
              >
                <Input.Password placeholder="Enter password" />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item
                name="emailId"
                label="Email"
                rules={[
                  {
                    required: true,
                    type: "email",
                    message: "Enter valid email",
                  },
                ]}
              >
                <Input placeholder="Enter email" />
              </Form.Item>
            </Col>
          </Row>

          <Row gutter={16}>
            <Col span={12}>
              <Form.Item
                name="phoneNo"
                label="Phone Number"
                rules={[{ required: true, message: "Enter phone number" }]}
              >
                <Input placeholder="Enter phone number" />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item
                name="role"
                label="Role Type"
                rules={[{ required: true, message: "Select role type" }]}
              >
                <Select placeholder="Select role type">
                  <Option value="ADMIN">Admin</Option>
                  <Option value="SUPERADMIN">Super Admin</Option>
                  <Option value="RECEPTIONIST">Receptionist</Option>
                </Select>
              </Form.Item>
            </Col>
          </Row>
          <Form.Item
            name="branchIds"
            label="Branches"
            rules={[{ required: true, message: "Select at least one branch" }]}
          >
            <Select
              mode="multiple"
              placeholder="Select branches"
              optionFilterProp="children"
            >
              {branchData?.data?.map((branch) => (
                <Option key={branch.branchId} value={branch.branchId}>
                  {branch.branchName} ({branch.address})
                </Option>
              ))}
            </Select>
          </Form.Item>

          <Form.Item name="permissions" label="Permissions">
            <Select
              mode="multiple"
              placeholder="Select permissions"
              options={PERMISSION_OPTIONS.map((perm) => ({
                label: perm,
                value: perm,
              }))}
            />
          </Form.Item>
        </Form>
      </Modal>
    </div>
  );
};

export default RoleManagement;
